import type { PreferredMemorizationMethod } from '@/features/students/types';

/**
 * A short, deterministic assessment for a student unsure which starter
 * method to pick (Phase 8, Pilar A). Every question is about learning
 * *pace and style*, not religious practice - this is engineering-level
 * categorization, never a fiqh decision, and the result is only ever a
 * suggestion: the student (or teacher/admin helping them) still chooses
 * and saves explicitly.
 */
export interface AssessmentQuestion {
  readonly id: string;
  readonly prompt: string;
  readonly options: readonly {
    readonly label: string;
    readonly weight: PreferredMemorizationMethod;
  }[];
}

export const ASSESSMENT_QUESTIONS: readonly AssessmentQuestion[] = [
  {
    id: 'new-material-pace',
    prompt: 'Bagaimana kamu lebih suka mempelajari hafalan baru?',
    options: [
      {
        label: 'Sedikit demi sedikit, diulang bersama guru sebelum lanjut',
        weight: 'TALAQQI_BERTAHAP',
      },
      { label: 'Saya suka menentukan target sendiri setiap hari', weight: 'TARGET_HARIAN_MANDIRI' },
      {
        label: 'Saya ingin lebih fokus menjaga hafalan yang sudah ada',
        weight: 'MURAJAAH_INTENSIF',
      },
    ],
  },
  {
    id: 'teacher-checkin',
    prompt: 'Seberapa sering kamu ingin dibimbing guru untuk bagian baru?',
    options: [
      { label: 'Setiap sesi, sebelum lanjut ke bagian berikutnya', weight: 'TALAQQI_BERTAHAP' },
      {
        label: 'Cukup berkala - saya kerjakan sendiri di antaranya',
        weight: 'TARGET_HARIAN_MANDIRI',
      },
      {
        label: 'Saya ingin lebih banyak waktu murajaah daripada hafalan baru',
        weight: 'MURAJAAH_INTENSIF',
      },
    ],
  },
  {
    id: 'biggest-challenge',
    prompt: 'Apa tantangan terbesarmu saat ini?',
    options: [
      { label: 'Sulit lanjut kalau belum benar-benar lancar', weight: 'TALAQQI_BERTAHAP' },
      { label: 'Butuh disiplin mengatur waktu sendiri', weight: 'TARGET_HARIAN_MANDIRI' },
      { label: 'Sering lupa hafalan yang sudah lama dihafal', weight: 'MURAJAAH_INTENSIF' },
    ],
  },
];

/**
 * Majority vote across the answered questions' weights; ties break toward
 * whichever weight was given first (the same order `ASSESSMENT_QUESTIONS`
 * lists), so the result is deterministic for a given set of answers.
 */
export function recommendMethod(
  answers: readonly PreferredMemorizationMethod[],
): PreferredMemorizationMethod | null {
  if (answers.length === 0) {
    return null;
  }

  const counts = new Map<PreferredMemorizationMethod, number>();
  let best: PreferredMemorizationMethod = answers[0] as PreferredMemorizationMethod;
  let bestCount = 0;

  for (const answer of answers) {
    const next = (counts.get(answer) ?? 0) + 1;
    counts.set(answer, next);
    if (next > bestCount) {
      bestCount = next;
      best = answer;
    }
  }

  return best;
}
